import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdviseFeatureInput, AdviseFeatureResponse } from '../../types/advise';
import { isNotFoundRefusal, refusalMessage } from '../../utils/refusalMessage';
import { fetchAdviseFeatureInput, updateAdviseFeatureResponseFlag } from './FeatureInput';

const FLAG_FAILED = "Couldn't change the answer's flag. Please try again.";
const ANSWER_REPLACED = 'That answer was replaced by a newer import, so the answers have been reloaded.';

export interface FlagNotice {
  message: string;
  severity: 'error' | 'info';
}

const flagFailed = (message: string): FlagNotice => ({ message, severity: 'error' });

// Flag is offered only on an answer that carries `flagged`, which only staff receive, and staff receive every answer,
// so the supplier's awaiting_review follows from its answers here.
const withFlagChange = (featureInput: AdviseFeatureInput, saved: AdviseFeatureResponse): AdviseFeatureInput => {
  const responses = featureInput.responses.map((row) => (row.id === saved.id ? saved : row));
  const awaiting = responses.some((row) => row.supplier_id === saved.supplier_id && row.flagged);
  return {
    ...featureInput,
    responses,
    suppliers: featureInput.suppliers.map((supplier) =>
      supplier.id === saved.supplier_id ? { ...supplier, awaiting_review: awaiting } : supplier
    )
  };
};

/**
 * The shortlist's requirement sheets and imported answers; null until loaded. A reload keeps the last load, which
 * `stale` marks as out of date after `loadFresh` until a newer one lands.
 */
export const useEngagementFeatureInput = (engagementId: string | null) => {
  const [featureInput, setFeatureInput] = useState<AdviseFeatureInput | null>(null);
  const [stale, setStale] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setFeatureInput(null);
  }, [engagementId]);

  useEffect(() => {
    setFailed(false);
    if (!engagementId) return;
    let current = true;
    const load = async () => {
      const { data, error } = await fetchAdviseFeatureInput(engagementId);
      if (!current) return;
      if (error || !data) {
        setFailed(true);
        return;
      }
      setFeatureInput(data);
      setStale(false);
    };
    load();
    return () => {
      current = false;
    };
  }, [engagementId, attempt]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);

  // For a view opened after the shortlist or the matrix may have changed; the last load is kept until a newer one lands.
  const loadFresh = useCallback(() => {
    setStale(true);
    reload();
  }, [reload]);

  // Flags the answer, or clears its flag; resolves to what to tell the Analyst, or null once the change shows.
  const setFlag = useCallback(
    async (response: AdviseFeatureResponse, flagged: boolean): Promise<FlagNotice | null> => {
      if (!engagementId) return flagFailed(FLAG_FAILED);
      const { data, error } = await updateAdviseFeatureResponseFlag(engagementId, response.id, flagged);
      if (isNotFoundRefusal(error)) {
        reload();
        return { message: ANSWER_REPLACED, severity: 'info' };
      }
      if (error || !data) return flagFailed(refusalMessage(error, FLAG_FAILED));
      setFeatureInput((prev) => prev && withFlagChange(prev, data));
      return null;
    },
    [engagementId, reload]
  );

  return useMemo(
    () => ({ featureInput, stale, failed, reload, loadFresh, setFlag }),
    [featureInput, stale, failed, reload, loadFresh, setFlag]
  );
};

export type EngagementFeatureInput = ReturnType<typeof useEngagementFeatureInput>;
